<template>
    <nav
        class="flex items-center border border-gray-200 bg-white pt-8 sticky top-0 z-40 md:block"
        aria-label="Breadcrumb"
    >
        <ol
            role="list"
            class="flex w-full min-h-12 max-w-full md:max-w-screen-xl space-x-2 md:space-x-4 px-2 sm:px-4 md:px-6 lg:px-8 mt-10 md:mt-0"
        >
            <li class="flex">
                <div class="flex items-center">
                    <a
                        href="/dashboard"
                        class="text-gray-400 hover:text-gray-500"
                    >
                        <svg
                            class="h-5 w-5 flex-shrink-0"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M9.293 2.293a1 1 0 011.414 0l7 7A1 1 0 0117 11h-1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-3a1 1 0 00-1-1H9a1 1 0 00-1 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-6H3a1 1 0 01-.707-1.707l7-7z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <span class="sr-only">Home</span>
                    </a>
                </div>
            </li>
            <li class="flex" v-for="(crumb, i) in crumbs" :key="i">
                <div v-if="crumb && !crumb?.loading" class="flex items-center">
                    <svg
                        class="h-full w-6 flex-shrink-0 text-gray-200"
                        viewBox="0 0 24 44"
                        preserveAspectRatio="none"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path d="M.293 0l22 22-22 22h1.414l22-22-22-22H.293z" />
                    </svg>
                    <router-link
                        v-if="['#', ''].indexOf(crumb.to) < 0"
                        :to="crumb.to"
                        class="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 rounded-sm focus:text-gray-700 transition-all"
                        v-html="crumb?.label"
                    ></router-link>
                    <span
                        class="ml-4 text-sm font-medium text-slate-400"
                        v-else
                        v-html="crumb?.label"
                    ></span>
                </div>
                <div v-else class="flex items-center">
                    <LoadingState
                        class="top-7 left-10"
                        :loading="crumb?.loading"
                        size="small"
                    />
                </div>
            </li>
        </ol>
    </nav>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAppStore } from '@/store/app';
import { useTripsStore } from '@/store/trips';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';

const route = useRoute();
const breadcrumbs = ref([]);
const appStore = useAppStore();
const tripsStore = useTripsStore();

const generateBreadcrumbs = async () => {
    const matchedRoutes = route.matched;

    breadcrumbs.value = await Promise.all(
        matchedRoutes.map(async (r) => {
            const breadcrumbKey = r.meta.breadcrumb;

            let breadcrumbName = appStore.breadcrumbs[breadcrumbKey];

            let loading = false;

            if (
                r.meta.breadcrumb &&
                appStore.breadcrumbs[r.meta.breadcrumb] === null
            ) {
                loading = true;
            }

            let isTrip = false;
            let to = r.path;

            if (r.name === 'Trip') {
                const tripUuid = route.params.tripUuid;
                if (tripsStore.activeTrip?.uuid !== tripUuid) {
                    const trip = await tripsStore.getTrip(tripUuid);
                    breadcrumbName = trip.title;
                } else {
                    breadcrumbName = tripsStore.activeTrip?.title || 'Trip';
                }
            }

            return {
                label: breadcrumbName ? breadcrumbName : r.meta.breadcrumb,
                to,
                loading,
                isTrip,
            };
        })
    );

    if (breadcrumbs.value.some((crumb) => crumb.isTrip)) {
        breadcrumbs.value.unshift({
            label: 'Trips',
            to: '/trips',
        });
    }
};

watch(
    () => [appStore.breadcrumbs, route],
    () => {
        generateBreadcrumbs();
    },
    {
        deep: true,
    }
);

function getRoutePath(match) {
    let path = '';
    let replaces = [];
    for (const [key, value] of Object.entries(route.params)) {
        replaces.push(`:${key}`);
    }

    if (replaces.length > 0) {
        path = match.path.replace(
            new RegExp(replaces.join('|'), 'gi'),
            function (matched) {
                const replacement = route.params[matched.replace(':', '')];
                return replacement ? replacement : matched;
            }
        );
    } else {
        path = match.path;
    }

    return path;
}

const crumbs = computed(() => {
    return route.matched.map((match) => {
        let breadcrumbName = appStore.breadcrumbs[match.meta.breadcrumb];
        let loading = false;

        if (
            match.meta.breadcrumb &&
            appStore.breadcrumbs[match.meta.breadcrumb] === null
        ) {
            loading = true;
        }

        const path = getRoutePath(match);

        return {
            label: breadcrumbName ? breadcrumbName : match.meta.title,
            to: path,
            loading,
        };
    });
});
</script>
